"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { Suspense, useEffect, useMemo, useRef } from "react";
import { captureWebEvent, identifyWebUser } from "@/lib/analytics/posthog";
import { authClient } from "@/lib/auth-client";

function PostHogPageViewContent() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthog = usePostHog();
  const { data: session } = authClient.useSession();
  const lastIdentifiedUserIdRef = useRef<string | null>(null);
  const searchParamsString = useMemo(
    () => searchParams.toString(),
    [searchParams],
  );
  const appSurface = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.location.hostname.startsWith("studio.")
        ? "studio"
        : "marketing",
    [],
  );

  // Track pageviews
  useEffect(() => {
    if (pathname && posthog) {
      try {
        let url = window.origin + pathname;
        if (searchParamsString) {
          url = `${url}?${searchParamsString}`;
        }
        captureWebEvent({
          posthog,
          event: "$pageview",
          properties: {
            $current_url: url,
          },
          appSurface,
        });
      } catch (error) {
        // Silently fail if PostHog capture fails (e.g., network issues, ad blockers)
        if (process.env.NODE_ENV === "development") {
          console.warn("PostHog pageview capture failed:", error);
        }
      }
    }
  }, [appSurface, pathname, posthog, searchParamsString]);

  // Identify user
  useEffect(() => {
    if (!posthog) return;

    const user = session?.user;
    if (user?.id) {
      if (lastIdentifiedUserIdRef.current === user.id) return;
      try {
        identifyWebUser({
          posthog,
          user: {
            id: user.id,
            email: user.email,
            name: user.name,
          },
        });
        lastIdentifiedUserIdRef.current = user.id;
      } catch (error) {
        // Silently fail if PostHog identify fails (e.g., network issues, ad blockers)
        if (process.env.NODE_ENV === "development") {
          console.warn("PostHog identify failed:", error);
        }
      }
      return;
    }

    // Only reset once when moving from identified -> anonymous.
    if (lastIdentifiedUserIdRef.current) {
      try {
        posthog.reset();
        lastIdentifiedUserIdRef.current = null;
      } catch (error) {
        // Silently fail if PostHog reset fails
        if (process.env.NODE_ENV === "development") {
          console.warn("PostHog reset failed:", error);
        }
      }
    }
  }, [posthog, session?.user?.email, session?.user?.id, session?.user?.name]);

  return null;
}

export default function PostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageViewContent />
    </Suspense>
  );
}
